import {
    Activity,
    AlertTriangle,
    ArrowDown,
    ArrowUp,
    Filter,
} from "lucide-react";
import React, { useState } from "react";
import cameraIcon from "../../assets/cctv_icon.png";
import { getStatusDetails, statusLegend } from "../../../utils/statusConfig";

export type StatusFilter = number | "all";

export interface LiveElevatorViewData {
    id: string; // from deviceName
    floor: number; // from currentFloor
    direction: "up" | "down" | "idle";
    status: number; // Mapped from ApiDeviceInfo.deviceStatus
    doorStatus: number;
    apiDeviceId: number; // Original deviceId
    cameraUrl?: string; // http cam feed
}

type LiveView360Props = {
    statusFilter: StatusFilter[];
    handleStatusFilterChange: (status: StatusFilter) => void;
    filteredElevators: LiveElevatorViewData[];
    setShowAssistant: React.Dispatch<React.SetStateAction<boolean>>;
    onShowCamera: (elevator: LiveElevatorViewData) => void;
};

const allElevatorsFilter = { id: "all" as const, name: "All Elevators", twColor: "bg-sky-500" };

export default function LiveView360({
    statusFilter,
    handleStatusFilterChange,
    filteredElevators,
    setShowAssistant,
    onShowCamera,
}: LiveView360Props) {
    const [videoErrors, setVideoErrors] = useState<{ [id: string]: boolean }>({});

    const statusFilters = [allElevatorsFilter, ...statusLegend.map(({ id, name, twColor }) => ({ id, name, twColor }))];

    return (
        <div className="space-y-6">
            {/* Status Filters */}
            <div className="flex items-center space-x-4 p-4 bg-slate-800/50 rounded-xl border border-slate-700/50">
                <Filter size={20} className="text-slate-400" />
                <div className="flex flex-wrap gap-2">
                    {statusFilters.map((filter) => (
                        <button
                            key={filter.id}
                            onClick={() => handleStatusFilterChange(filter.id)}
                            className={`px-4 py-2 rounded-lg flex items-center space-x-2 transition-all ${
                                statusFilter.includes(filter.id)
                                    ? "bg-slate-700 text-white ring-2 ring-slate-500"
                                    : "bg-slate-800 text-slate-400 hover:bg-slate-700"
                            }`}
                        >
                            <span className={`w-2 h-2 rounded-full ${filter.twColor}`} />
                            <span>{filter.name}</span>
                        </button>
                    ))}
                </div>
            </div>

            {/* Elevator Grid */}
            <div className="grid grid-cols-4 gap-6">
                {filteredElevators.map((elevator) => {
                    const statusDetails = getStatusDetails(elevator.status);

                    return (
                        <div
                            key={elevator.id}
                            className="bg-slate-800/80 backdrop-blur-sm rounded-xl p-6 border border-slate-700/50"
                        >
                            <div className="flex justify-between items-center mb-6">
                                {/* Elevator ID */}
                                <div
                                    className="text-lg font-medium text-white truncate"
                                    title={elevator.id}
                                    style={{ minWidth: 0 }}
                                >
                                    {elevator.id}
                                </div>

                                {/* Floor number and direction arrow */}
                                <div className="flex items-center space-x-2">
                                    <span className="text-lg font-medium text-white">
                                        {elevator.floor}
                                    </span>
                                    {elevator.direction === "up" && (
                                        <ArrowUp className="text-emerald-500 w-6 h-6" />
                                    )}
                                    {elevator.direction === "down" && (
                                        <ArrowDown className="text-red-500 w-6 h-6" />
                                    )}
                                    {elevator.direction === "idle" && (
                                        <Activity className="text-slate-400 w-6 h-6" />
                                    )}
                                </div>

                                {/* Camera button */}
                                <button
                                    type="button"
                                    className={`w-10 h-10 flex items-center justify-center rounded-lg ${statusDetails?.twColor}`}
                                    title={`Status: ${statusDetails?.name}`}
                                    style={{ padding: 0, border: "none" }}
                                    onClick={() => onShowCamera(elevator)}
                                >
                                    <img src={cameraIcon} alt="Camera" className="w-6 h-6" />
                                </button>
                            </div>

                            <div className="flex items-center justify-center h-40 relative mb-6">
                                <div className="w-full h-full bg-slate-900/70 rounded-xl flex items-center justify-center border border-slate-700/50 shadow-inner">
                                    {/* Camera Frame: Video or SVG fallback */}
                                    {elevator.cameraUrl && !videoErrors[elevator.id] ? (
                                        <iframe
                                            title={elevator.id}
                                            className="w-full h-full rounded-lg border-2 border-slate-600 object-cover"
                                            src={elevator.cameraUrl}
                                            onError={() => {
                                                console.log(`ERROR WITH CAM: ${elevator.id}`);
                                                setVideoErrors((prev) => ({
                                                    ...prev,
                                                    [elevator.id]: true,
                                                }));
                                            }}
                                        >
                                            {/* Fallback for browsers that don't support video */}
                                            Your browser does not support the video tag.
                                        </iframe>
                                    ) : (
                                        <div className="w-24 h-16 bg-slate-700/60 rounded-lg flex flex-col items-center justify-center border-2 border-slate-600">
                                            {/* Camera Icon Composition */}
                                            <svg
                                                width="32"
                                                height="32"
                                                fill="none"
                                                viewBox="0 0 24 24"
                                                stroke="currentColor"
                                                className="text-slate-400 mb-1"
                                            >
                                                <rect
                                                    x="4"
                                                    y="7"
                                                    width="16"
                                                    height="10"
                                                    rx="2"
                                                    strokeWidth="2"
                                                />
                                                <circle cx="12" cy="12" r="3" strokeWidth="2" />
                                                <rect
                                                    x="16"
                                                    y="9"
                                                    width="4"
                                                    height="6"
                                                    rx="1"
                                                    strokeWidth="2"
                                                />
                                            </svg>
                                            <span className="text-xs text-slate-400">No Feed</span>
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="space-y-3 text-sm">
                                <div className="flex justify-between items-center p-3 rounded-lg bg-slate-700/50">
                                    <span className="text-slate-300">Door Status</span>
                                    <span className="font-medium text-white">
                                        {elevator.doorStatus === 10 ? "Open" : "Closed"}
                                    </span>
                                </div>
                                <div className="flex justify-between items-center p-3 rounded-lg bg-slate-700/50">
                                    <span className="text-slate-300">Status</span>
                                    <span
                                        className="font-medium text-white truncate"
                                        title={statusDetails?.name}
                                    >
                                        {statusDetails?.name}
                                    </span>
                                </div>
                            </div>

                            {statusDetails?.name !== 'Running' && (
                                <button
                                    onClick={() => setShowAssistant(true)}
                                    className="mt-6 w-full py-3 px-4 bg-amber-500/10 text-amber-500 rounded-lg text-sm flex items-center justify-center space-x-2 hover:bg-amber-500/20 transition-colors"
                                >
                                    <AlertTriangle size={16} />
                                    <span>View Issue Details</span>
                                </button>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
